import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelope, faPhone } from '@fortawesome/free-solid-svg-icons';
import Header from './component/Header';
import PublicFooter from '../components/layout/PublicFooter';
import StaticPageLayout from '../components/layout/StaticPageLayout';
import { Button, Card } from '../components/ui';
import { API_URL } from '../config';
import Swal from 'sweetalert2';
import SEO from '../components/SEO';

const ContactUs = () => {
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
    });

    const [formErrors, setFormErrors] = useState({});

    const validateForm = () => {
        const errors = {};
        let valid = true;

        if (!formData.name) {
            errors.name = 'Name is required';
            valid = false;
        }
        if (!formData.email) {
            errors.email = 'Email is required';
            valid = false;
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Invalid email address';
            valid = false;
        }
        if (!formData.message) {
            errors.message = 'Message is required';
            valid = false;
        }

        setFormErrors(errors);
        return valid;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (validateForm()) {
            fetch(API_URL + '/contact-us', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData),
            })
                .then((response) => response.json())
                .then((data) => {
                    if (data.errors) {
                        const errorMessages = Object.values(data.errors).join('<br>');
                        Swal.fire({ title: 'Error!', html: errorMessages, icon: 'error', confirmButtonText: 'OK' });
                    } else {
                        Swal.fire({ title: 'Success!', html: data.message, icon: 'success', confirmButtonText: 'OK' });
                    }
                })
                .catch((error) => console.error('Error:', error));

            setFormData({ name: '', email: '', message: '' });
            setFormErrors({});
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({ ...formData, [name]: value });
        if (formErrors[name]) {
            setFormErrors({ ...formErrors, [name]: '' });
        }
    };

    return (
        <div style={{ fontFamily: 'Inter, system-ui, -apple-system, sans-serif' }}>
            <SEO title="Contact Us" description="Get in touch with RMT. Questions about vehicle tag renewal, payment methods, or your account? Reach out and we'll respond within 24 hours." canonical="/contact/us" />
            <Header />
            <StaticPageLayout
                title="Contact Us"
                subtitle="Have a question or need assistance? We're here to help."
            >
                <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-5 tw-gap-8 tw-max-w-5xl">
                    {/* Form */}
                    <div className="md:tw-col-span-3">
                        <Card variant="outlined">
                            <form onSubmit={handleSubmit} className="tw-space-y-5">
                                <div className="tw-flex tw-flex-col tw-gap-1.5">
                                    <label htmlFor="name" className="tw-text-sm tw-font-medium tw-text-gray-700">Name</label>
                                    <input
                                        type="text"
                                        id="name"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        className={`tw-w-full tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100 ${formErrors.name ? 'tw-border-error' : 'tw-border-gray-400'}`}
                                    />
                                    {formErrors.name && <p className="tw-text-xs tw-text-error tw-m-0">{formErrors.name}</p>}
                                </div>

                                <div className="tw-flex tw-flex-col tw-gap-1.5">
                                    <label htmlFor="email" className="tw-text-sm tw-font-medium tw-text-gray-700">Email</label>
                                    <input
                                        type="email"
                                        id="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleChange}
                                        className={`tw-w-full tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100 ${formErrors.email ? 'tw-border-error' : 'tw-border-gray-400'}`}
                                    />
                                    {formErrors.email && <p className="tw-text-xs tw-text-error tw-m-0">{formErrors.email}</p>}
                                </div>

                                <div className="tw-flex tw-flex-col tw-gap-1.5">
                                    <label htmlFor="message" className="tw-text-sm tw-font-medium tw-text-gray-700">Message</label>
                                    <textarea
                                        id="message"
                                        name="message"
                                        rows={5}
                                        value={formData.message}
                                        onChange={handleChange}
                                        className={`tw-w-full tw-rounded-lg tw-border tw-bg-white tw-px-4 tw-py-3 tw-text-sm tw-text-gray-800 tw-placeholder-gray-400 tw-outline-none tw-transition-all tw-resize-y focus:tw-border-primary-500 focus:tw-ring-2 focus:tw-ring-primary-100 ${formErrors.message ? 'tw-border-error' : 'tw-border-gray-400'}`}
                                    />
                                    {formErrors.message && <p className="tw-text-xs tw-text-error tw-m-0">{formErrors.message}</p>}
                                </div>

                                <Button type="submit" variant="primary" size="lg" className="tw-w-full">
                                    Send Message
                                </Button>
                            </form>
                        </Card>
                    </div>

                    {/* Contact Info */}
                    <div className="md:tw-col-span-2 tw-space-y-4">
                        <Card variant="flat" className="tw-text-center">
                            <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-primary-50 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                                <FontAwesomeIcon icon={faEnvelope} className="tw-text-primary-500 tw-text-lg" />
                            </div>
                            <h4 className="tw-text-xl tw-text-gray-900 tw-m-0 tw-mb-1">Email Us</h4>
                            <a href="mailto:hello@renewmytags.com" className="tw-text-primary-600 hover:tw-text-primary-700 tw-no-underline tw-text-sm">
                                hello@renewmytags.com
                            </a>
                        </Card>

                        <Card variant="flat" className="tw-text-center">
                            <div className="tw-w-12 tw-h-12 tw-rounded-xl tw-bg-primary-50 tw-flex tw-items-center tw-justify-center tw-mx-auto tw-mb-4">
                                <FontAwesomeIcon icon={faPhone} className="tw-text-primary-500 tw-text-lg" />
                            </div>
                            <h4 className="tw-text-xl tw-text-gray-900 tw-m-0 tw-mb-1">Call Us</h4>
                            <a href="tel:800-778-9457" className="tw-text-primary-600 hover:tw-text-primary-700 tw-no-underline tw-text-sm">
                                800-778-9457
                            </a>
                        </Card>
                    </div>
                </div>
            </StaticPageLayout>
            <PublicFooter />
        </div>
    );
};

export default ContactUs;
